import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";
import InputFieldBox from "../../../common/InputFieldBox";
import InputFieldAutoComplete from "../../../common/InputFieldAutoComplete";
import PwgPlanSelect from "../PwgPlanSelect";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { CLIENTS } from "../../../../constants/value";
import { __imsiWorkflowPWG, __getOrganizations } from "../../../../api/sim";

// ESN,Areacode,Zipcode,Street1,Street2,City,State,Wifi Calling Zipcode[,BillingCode]
const MIN_COLUMN_COUNT = 8;
const MAX_COLUMN_COUNT = 9;
const LINE_FORMAT =
  "ESN,Areacode,Zipcode,Street1,Street2,City,State,Wifi Calling Zipcode,BillingCode(optional)";

const IMSIWorkflowForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [planId, setPlanId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [provider, setProvider] = useState("");
  const [isLoaner, setIsLoaner] = useState(false);
  const [isVoice, setIsVoice] = useState(false);
  const { setAlertMessage } = useAlert();
  const [response, call, loading] = useAxios({
    ...__imsiWorkflowPWG,
  });
  const [organizationResponse, organizationCall, organizationLoading] = useAxios({
    ...__getOrganizations,
  });
  const nav = useNavigate();

  const lineValidationSchema = yup.object({
    esn: yup
      .string("esn")
      .matches(/^\d{19,25}$/, "ESN should be 19 to 25 digits long")
      .required(),
    areacode: yup
      .string("areacode")
      .test("Digits only", "The field should have 3 digits only", (value) =>
        value && value.length ? /^\d{3}$/.test(value) : true
      )
      .optional(),
    zipcode: yup
      .string("zipcode")
      .test("Digits only", "The field should have 5 digits only", (value) =>
        value && value.length ? /^\d{5}$/.test(value) : true
      )
      .optional(),
    street1: yup.string("street1").required(),
    street2: yup.string("street2").optional(),
    city: yup.string("city").required(),
    state: yup.string("state").required(),
    wifi_calling_zipcode: yup
      .string("wifi_calling_zipcode")
      .matches(/^\d{5}$/, "Wifi Calling Zipcode should be 5 digits")
      .required(),
    billing_code: yup.string("billing_code").optional(),
  });

  useEffect(() => {
    organizationCall({ params: { client: CLIENTS.sparq, is_voice: isVoice } });
  }, [isVoice]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "PWG IMSI Workflow queue started" });
      setLinesData([]);
      nav("/pwg/imsi_workflow");
    }
    setSubmitting(false);
  }, [response]);

  useEffect(() => {
    if (organizationResponse && organizationResponse.status) {
      setOrganizations(organizationResponse.data);
    }
  }, [organizationResponse]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];

    if (!data) {
      return errors;
    }

    const lines = data.split("\n");
    lines.some((line, idx) => {
      if (!line.trim()) {
        return false; // Skip empty lines
      }

      try {
        const line_data = line.split(",");
        if (
          line_data.length < MIN_COLUMN_COUNT ||
          line_data.length > MAX_COLUMN_COUNT
        ) {
          errors.lines = `Invalid format in line ${idx + 1}: Expected ${MIN_COLUMN_COUNT} or ${MAX_COLUMN_COUNT} fields, got ${line_data.length}`;
          return true;
        }
        const obtainedData = {
          esn: line_data[0].trim(),
          areacode: line_data[1].trim(),
          zipcode: line_data[2].trim(),
          street1: line_data[3].trim(),
          street2: line_data[4].trim(),
          city: line_data[5].trim(),
          state: line_data[6].trim(),
          wifi_calling_zipcode: line_data[7].trim(),
          billing_code: (line_data[8] || "").trim(),
        };

        lineValidationSchema.validateSync(obtainedData);

        // send blanks as null so optional fields stay unset on the backend
        formatedLines.push(
          Object.keys(obtainedData).reduce((obj, key) => {
            obj[key] = obtainedData[key] || null;
            return obj;
          }, {})
        );
      } catch (err) {
        console.log(err);
        errors.lines = `Invalid format in line ${idx + 1}: ${err.message || line}`;
        return true;
      }

      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { lines: [] },
    onSubmit: () => {},
    validateOnChange: true,
  });

  const handleSubmit = () => {
    if (loading || submitting) return;
    setSubmitting(true);
    call({
      data: {
        plan_id: planId,
        iqsim_organization: currentOrganization,
        provider: provider || null,
        is_loaner: isLoaner,
        is_voice: isVoice,
        lines: [...linesData],
      },
    });
  };

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };
  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    // Check if user has entered the file
    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors.lines,
          upload: "Please input a csv file",
        });
        return;
      }

      handleParse(inputFile);
    }
  };

  if (loading || organizationLoading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="PWG IMSI Workflow"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <PwgPlanSelect value={planId} onChange={setPlanId} />
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <InputFieldAutoComplete
                isSelect
                options={[
                  ...organizations.reduce((acc, org) => {
                    const val = {
                      value: org.name,
                      label: org.name,
                    };
                    if (org.is_active) acc.push(val);
                    return acc;
                  }, []),
                ]}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                name="organization"
                label="Organization"
                id="organization"
                onChange={(e, value) =>
                  setCurrentOrganization(value?.value || null)
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <InputFieldBox
                type="text"
                id="provider"
                name="provider"
                label="Provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <InputFieldBox
                isCheckbox={true}
                value={isLoaner}
                onChange={(event) => setIsLoaner(event.target.checked)}
                label="Is Loaner"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <InputFieldBox
                isCheckbox={true}
                value={isVoice}
                onChange={(event) => {
                  setIsVoice(event.target.checked);
                  setCurrentOrganization(null);
                }}
                label="Is Voice"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.upload) ? true : false}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors.upload) ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="lines">
              {"Add in the format"}
              <br></br>
              {LINE_FORMAT}
              <br></br>
              {"The address is sent as the E911 address on activation to enable WiFi Calling."}
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors.lines) ? true : false}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={`Add in the format\n${LINE_FORMAT}`}
                style={{ minWidth: 550 }}
                name="lines"
                minRows={5}
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
              />
              {Boolean(formik.errors.lines) ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  Boolean(formik.errors.lines) ||
                  !planId ||
                  !currentOrganization ||
                  !linesData.length
                }
                onClick={handleSubmit}
                size="large"
                loading={loading}
                variant="contained"
              >
                {"IMSI Workflow"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default IMSIWorkflowForm;
